import React, { useMemo } from 'react';
import { Paper, Text, Avatar, Tooltip, SimpleGrid, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { User } from '../../types';
import {
  PairsMap,
  computeLoad,
  computeMetrics,
  loadBand,
  LoadBand,
} from './assignmentsMatrix.helpers';
import classes from './AssignmentsMatrixView.module.css';

interface AssignmentsMatrixViewProps {
  participantIds: string[];
  usersById: Record<string, User>;
  pairs: PairsMap;
  requestedReviewersBySubject: Record<string, string[]>;
  onToggle: (subjectId: string, reviewerId: string) => void;
}

const BAND_COLOR: Record<LoadBand, string> = {
  idle: 'var(--mantine-color-gray-5)',
  balanced: 'var(--mantine-color-teal-6)',
  high: 'var(--mantine-color-orange-6)',
  overload: 'var(--mantine-color-red-6)',
};

const stickyLeft: React.CSSProperties = { position: 'sticky', left: 0 };

function nameOf(usersById: Record<string, User>, id: string): string {
  return usersById[id]?.name ?? id.slice(0, 6);
}

function MetricCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: React.ReactNode;
  sub: string;
  color?: string;
}) {
  return (
    <Paper withBorder p="md" radius="md">
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text
        fw={700}
        style={{ fontSize: 26, lineHeight: 1.1, color, fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </Text>
      <Text size="xs" c="dimmed" mt={4}>
        {sub}
      </Text>
    </Paper>
  );
}

function MatrixCell({
  isSelf,
  isOn,
  isWished,
  isOver,
  onClick,
  toggleLabel,
}: {
  isSelf: boolean;
  isOn: boolean;
  isWished: boolean;
  isOver: boolean;
  onClick?: () => void;
  toggleLabel: string;
}) {
  let content: React.ReactNode = null;
  if (isSelf) {
    content = (
      <span
        style={{ width: 10, height: 2, borderRadius: 2, background: 'var(--mantine-color-gray-4)' }}
      />
    );
  } else if (isOn && isWished) {
    content = (
      <span
        style={{
          width: 14,
          height: 14,
          borderRadius: '50%',
          background: 'var(--mantine-color-teal-6)',
          boxShadow: '0 0 0 3px var(--mantine-color-teal-light)',
        }}
      />
    );
  } else if (isOn) {
    content = (
      <span
        style={{
          width: 13,
          height: 13,
          borderRadius: '50%',
          border: '2px solid var(--mantine-color-teal-6)',
        }}
      />
    );
  }
  const isClickable = !isSelf;
  return (
    <div
      onClick={onClick}
      title={isClickable ? toggleLabel : undefined}
      className={`${classes.cell} ${isClickable ? classes.clickable : ''}`}
      style={{
        background: isOver
          ? 'var(--mantine-color-red-light)'
          : isSelf
            ? 'var(--mantine-color-default-hover)'
            : undefined,
      }}
    >
      {content}
    </div>
  );
}

function Legend() {
  const { t } = useTranslation();
  const item = (swatch: React.ReactNode, label: string) => (
    <Group gap={6} wrap="nowrap">
      {swatch}
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </Group>
  );
  return (
    <Group gap="lg" mt="md" wrap="wrap">
      {item(
        <span
          style={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: 'var(--mantine-color-teal-6)',
          }}
        />,
        t('perfReview.legendAssignedWished'),
      )}
      {item(
        <span
          style={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            border: '2px solid var(--mantine-color-teal-6)',
          }}
        />,
        t('perfReview.legendAssignedManual'),
      )}
      {item(
        <span
          style={{
            width: 12,
            height: 12,
            borderRadius: 3,
            background: 'var(--mantine-color-orange-6)',
          }}
        />,
        t('perfReview.legendHighLoad'),
      )}
      {item(
        <span
          style={{
            width: 12,
            height: 12,
            borderRadius: 3,
            background: 'var(--mantine-color-red-6)',
          }}
        />,
        t('perfReview.legendOverload'),
      )}
    </Group>
  );
}

export const AssignmentsMatrixView: React.FC<AssignmentsMatrixViewProps> = ({
  participantIds,
  usersById,
  pairs,
  requestedReviewersBySubject,
  onToggle,
}) => {
  const { t } = useTranslation();
  const load = useMemo(() => computeLoad(pairs, participantIds), [pairs, participantIds]);
  const metrics = useMemo(() => computeMetrics(pairs, participantIds), [pairs, participantIds]);

  if (participantIds.length === 0) {
    return (
      <Text c="dimmed" ta="center" py="xl">
        {t('perfReview.emptyAssignmentsHint')}
      </Text>
    );
  }

  const n = participantIds.length;
  const gridTemplateColumns = `200px repeat(${n}, minmax(40px, 1fr)) 56px`;
  const border = '1px solid var(--mantine-color-default-border)';
  const headerBg = 'var(--mantine-color-default-hover)';

  return (
    <div>
      <SimpleGrid cols={{ base: 2, sm: 4 }} p="md" spacing="md">
        <MetricCard
          label={t('perfReview.metricTotal')}
          value={metrics.total}
          sub={t('perfReview.metricTotalSub')}
        />
        <MetricCard
          label={t('perfReview.metricAvg')}
          value={metrics.avg.toFixed(1)}
          sub={t('perfReview.metricAvgSub')}
        />
        <MetricCard
          label={t('perfReview.metricOverloaded')}
          value={metrics.overloaded}
          sub={t('perfReview.metricOverloadedSub')}
          color={metrics.overloaded ? 'var(--mantine-color-red-6)' : undefined}
        />
        <MetricCard
          label={t('perfReview.metricUnused')}
          value={metrics.unused}
          sub={t('perfReview.metricUnusedSub')}
          color={metrics.unused ? 'var(--mantine-color-orange-6)' : undefined}
        />
      </SimpleGrid>

      <div style={{ overflowX: 'auto', padding: '0 16px 16px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns,
            minWidth: 'min-content',
            border,
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              ...stickyLeft,
              zIndex: 3,
              background: 'var(--mantine-color-body)',
              borderBottom: border,
              padding: '8px 10px',
              display: 'flex',
              alignItems: 'flex-end',
              fontSize: 10,
              fontWeight: 600,
              lineHeight: 1.3,
              color: 'var(--mantine-color-dimmed)',
            }}
          >
            {t('perfReview.matrixAxisHint')}
          </div>
          {participantIds.map((rid) => {
            const rload = load[rid] || 0;
            const isOver = rload >= 6;
            return (
              <Tooltip
                key={rid}
                withArrow
                label={`${nameOf(usersById, rid)} · ${t('perfReview.matrixReviewsCount', { count: rload })}`}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 3,
                    padding: '8px 2px',
                    borderBottom: border,
                    borderLeft: border,
                    background: isOver
                      ? 'var(--mantine-color-red-light)'
                      : rload === 5
                        ? 'var(--mantine-color-orange-light)'
                        : headerBg,
                  }}
                >
                  <Avatar
                    src={usersById[rid]?.avatar}
                    name={nameOf(usersById, rid)}
                    color="initials"
                    size={26}
                    radius="xl"
                  />
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: BAND_COLOR[loadBand(rload)],
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {rload}
                  </span>
                </div>
              </Tooltip>
            );
          })}
          <div
            style={{
              padding: '8px 4px',
              borderBottom: border,
              borderLeft: border,
              background: headerBg,
              fontSize: 10,
              fontWeight: 600,
              textAlign: 'center',
              alignSelf: 'center',
              color: 'var(--mantine-color-dimmed)',
            }}
          >
            {t('perfReview.matrixCoverage')}
          </div>

          {participantIds.map((sid) => {
            const wished = new Set(requestedReviewersBySubject[sid] || []);
            const reviewers = new Set(pairs[sid] || []);
            const coverage = participantIds.filter((rid) => reviewers.has(rid)).length;
            return (
              <React.Fragment key={sid}>
                <div
                  style={{
                    ...stickyLeft,
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '0 10px',
                    borderTop: border,
                    background: 'var(--mantine-color-body)',
                  }}
                >
                  <Avatar
                    src={usersById[sid]?.avatar}
                    name={nameOf(usersById, sid)}
                    color="teal"
                    size={24}
                    radius="xl"
                  />
                  <div style={{ minWidth: 0 }}>
                    <Text size="xs" fw={600} truncate>
                      {nameOf(usersById, sid)}
                    </Text>
                    <Text style={{ fontSize: 10 }} c="dimmed" truncate>
                      {usersById[sid]?.position ?? ''}
                    </Text>
                  </div>
                </div>
                {participantIds.map((rid) => (
                  <MatrixCell
                    key={rid}
                    isSelf={rid === sid}
                    isOn={reviewers.has(rid)}
                    isWished={wished.has(rid)}
                    isOver={(load[rid] || 0) >= 6}
                    onClick={rid === sid ? undefined : () => onToggle(sid, rid)}
                    toggleLabel={t('perfReview.matrixToggleTooltip')}
                  />
                ))}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderTop: border,
                    borderLeft: border,
                  }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                      color:
                        coverage < 3
                          ? 'var(--mantine-color-orange-6)'
                          : 'var(--mantine-color-text)',
                    }}
                  >
                    {coverage}
                  </span>
                </div>
              </React.Fragment>
            );
          })}
        </div>
        <Legend />
      </div>
    </div>
  );
};
